import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import clsx from 'clsx';

import { DEFAULT_FETCHED_DATA_TTL } from 'constants/time';
import { Spinner } from 'components/ui';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
    getNotifications,
    getFriendRequests,
    setNotificationsLastFetched,
    markViewsRead,
    markNotificationsRead,
} from 'redux/notifications/actions';
import {
    hasMoreNotifications,
    selectFriendRequests,
    selectLastFetchTimestamp,
    selectNotifications,
} from 'redux/notifications/selectors';
import { getGuests } from 'redux/guests/actions';
import { getUserCounters } from 'redux/counters/actions';
import { selectAccountId } from 'redux/account/selectors';
import { selectGuests } from 'redux/guests/selectors';
import { selectCounters } from 'redux/counters/selectors';
import NotificationCounter from 'components/NotificationCounter';
import Typography from 'components/ui/Typography';
import Icon from 'components/Icon';

import Notifications from './Notifications';
import FriendRequestsNotifications from './FriendRequestsNotifications';
import ViewsNotifications from './ViewsNotifications';

import css from './AlertsMenu.module.scss';

interface Props {
    isPopover?: boolean;
}

const AlertsMenu: React.FC<Props> = ({ isPopover = false }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const { new_views: newViews, new_notifications: newNotifications } = useAppSelector(selectCounters);
    const notifications = useAppSelector(selectNotifications);
    const friendRequests = useAppSelector(selectFriendRequests);
    const guests = useAppSelector(selectGuests);
    const hasMore = useAppSelector(hasMoreNotifications);
    const timestamp = useAppSelector(selectLastFetchTimestamp);

    const [isGuestsLoaded, setGuestsLoaded] = useState(!!guests?.length);
    const [isNotificationsLoaded, setNotificationsLoaded] = useState(false);
    const [isRequestsLoaded, setRequestsLoaded] = useState(false);

    const noNotifications =
        notifications.length === 0 && friendRequests.length === 0 && (guests?.length === 0 || guests === undefined);

    const currentTime = new Date();

    const filteredNotifications = notifications?.filter(
        (notification) => notification.type !== 'FRIEND_REQUEST_MESSAGE' && notification.user !== null,
    );

    const loadMore = () => {
        dispatch(getNotifications({ offset: notifications?.length || 0 }));
    };

    const accountId = useAppSelector(selectAccountId);

    const markAllRead = () => {
        newViews > 0 && dispatch(markViewsRead(accountId));
        newNotifications > 0 && dispatch(markNotificationsRead());
    };

    useEffect(() => {
        if (notifications.length === 0 && friendRequests.length === 0) {
            dispatch(getNotifications({ offset: 0 })).then(() => setNotificationsLoaded(true));
            !guests?.length && dispatch(getGuests()).then(() => setGuestsLoaded(true));
            dispatch(getFriendRequests()).then(() => setRequestsLoaded(true));
            dispatch(getUserCounters());
            dispatch(setNotificationsLastFetched(new Date().getTime()));
        }

        if (timestamp && differenceInMinutes(currentTime, timestamp) >= DEFAULT_FETCHED_DATA_TTL) {
            dispatch(getNotifications({ offset: notifications.length })).then(() => setNotificationsLoaded(true));
            !guests?.length && dispatch(getGuests()).then(() => setGuestsLoaded(true));
            dispatch(getFriendRequests()).then(() => setRequestsLoaded(true));
            dispatch(getUserCounters());
        }
    }, []);

    const isAllSectionsLoaded = isGuestsLoaded && isNotificationsLoaded && isRequestsLoaded;

    useEffect(() => {
        if (isAllSectionsLoaded) {
            markAllRead();
        }
    }, [isAllSectionsLoaded]);

    useEffect(() => {
        const listContainer = document.getElementById('scrollContainer');

        if (
            listContainer &&
            hasMore &&
            notifications.length &&
            listContainer.scrollHeight <= listContainer.clientHeight
        ) {
            loadMore();
        }
    }, [notifications]);

    return (
        <div className={clsx(css.menuContainer, noNotifications && css.empty, isPopover && css['popover'])}>
            {noNotifications ? (
                <div className={css.banner}>
                    {/* if three sections of alerts menu is loaded then remove spinner*/}
                    {!isAllSectionsLoaded ? (
                        <Spinner />
                    ) : (
                        <>
                            <Icon color="gray2" width={35} name="bell-alert" />
                            <Typography color="gray2">{t('AlertsMenu.NoNotifications')}</Typography>
                        </>
                    )}
                </div>
            ) : (
                <InfiniteScroll
                    scrollableTarget="scrollContainer"
                    dataLength={filteredNotifications?.length || 0}
                    next={loadMore}
                    hasMore={hasMore}
                    loader={<Spinner key="spinner" />}
                >
                    {friendRequests && friendRequests.length > 0 && (
                        <>
                            <div className={css.header}>
                                <Typography style="HeadMedSemibold">{t('Common.Sections.FriendRequests')}</Typography>
                                <NotificationCounter count={String(friendRequests.length)} />
                            </div>
                            <FriendRequestsNotifications friendRequests={friendRequests} />
                        </>
                    )}
                    {guests && guests.length > 0 && (
                        <>
                            <div className={css.header}>
                                <Typography style="HeadMedSemibold">{t('Common.Sections.Views')}</Typography>
                                {newViews > 0 && <NotificationCounter count={newViews} />}
                            </div>
                            <ViewsNotifications views={guests} />
                        </>
                    )}
                    {filteredNotifications && filteredNotifications.length > 0 && (
                        <>
                            <div className={css.header}>
                                <Typography style="HeadMedSemibold">{t('Common.Sections.Notifications')}</Typography>
                                {newNotifications > 0 && <NotificationCounter count={newNotifications} />}
                            </div>
                            <Notifications notifications={filteredNotifications} />
                        </>
                    )}
                </InfiniteScroll>
            )}
        </div>
    );
};

export default React.memo(AlertsMenu);
